import { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import AgGrid from "../../../UI/AgGrid";
import {
  ColDef,
  GridReadyEvent,
  RowDoubleClickedEvent,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { useDeliveryOrderColumnDefs } from "./columnDefs";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import { DELIVERY_ORDER_VIEW_AGGRID } from "../../../../services/AgGrid/LogisticAgGrid";

interface Props {
  isFilter?: string | null;
  referenceUniqueId?: string;
}

const DeliveryOrderTable = ({ isFilter, referenceUniqueId }: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const navigate = useNavigate();

  const branch_id = parseInt(sessionStorage.getItem("branch-id") || "");

  const columnDef: ColDef[] | undefined = useDeliveryOrderColumnDefs();

  const graphQLClientWithHeader: GraphQLClient =
    createGraphQLClientWithMiddleware("logistic");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const {
        created_date,
        issue_date,
        exported_date,
        delivery_date,
        created_by_object,
        related_user_object_list,
        reference_document_list,
        last_updated_date,
        ...otherFilterModel
      } = filterModel;
      const formatFilter = {
        ...otherFilterModel,
        created_date: dateFilterModel(created_date),
        issue_date: dateFilterModel(issue_date),
        exported_date: dateFilterModel(exported_date),
        delivery_date: dateFilterModel(delivery_date),
        created_by: created_by_object,
        last_updated_date: dateFilterModel(last_updated_date),
        related_user_list: related_user_object_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: related_user_object_list.values,
            }
          : undefined,
        reference_document_list: referenceUniqueId
          ? {
              filterType: "array",
              type: "hasSome",
              values: [referenceUniqueId],
            }
          : reference_document_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: [reference_document_list.filter],
            }
          : undefined,
        branch_id:
          branch_id !== -1
            ? {
                filterType: "number",
                type: "equals",
                filter: branch_id,
              }
            : undefined,
      };

      try {
        const { DeliveryOrderViewsAggrid } =
          await graphQLClientWithHeader.request(DELIVERY_ORDER_VIEW_AGGRID, {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          });
        params.success({
          rowData: DeliveryOrderViewsAggrid.results as any[],
          rowCount: DeliveryOrderViewsAggrid.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("status");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "issued":
          instance?.setModel({ values: ["issued"] });
          break;
        case "delivering":
          instance?.setModel({ values: ["delivering"] });
          break;
        case "delivery_success":
          instance?.setModel({ values: ["delivery_success"] });
          break;
        case "delivery_fail":
          instance?.setModel({ values: ["delivery_fail"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({});
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params: GridReadyEvent) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(`/logistic/delivery-order/${encodeURIComponent(params.data.id)}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/logistic/delivery-order"}
    />
  );
};

export default DeliveryOrderTable;
